import React, { useEffect, useState, useRef } from "react"
import { useFormikContext } from "formik"
import { FieldError, useFieldCSSClasses } from "./FieldError"
import useHideAndShowField from "./hooks/useHideAndShowField"
import { isRLTLang } from "src/i18n"
import { getAttr } from "src/helpers"
import { isFunction, debounce } from "lodash"

const Counter = ({
  field,
  form: { touched, errors },
  label,
  unit = '',
  inputGroupClassName = "form-group",
  inputClassName = "form-control",
  controller = undefined,
  siblingsControl = undefined,
  type = "text",
  required = false,
  disabled = false,
  maxLength = 256,
  form,
  size = 12,
  placeholder,
  loadValue,
  step = 1,
  enableInput = false,
  maxCounterValue = 20,
  ...props
}) => {

  const unitStyle = isRLTLang()
    ? {
      position: "absolute",
      height: '1.125rem',
      minWidth: '1.25rem',
      paddingTop: '.375rem',
      left: '1.375rem'
    }
    : {
      position: "absolute",
      height: '1.125rem',
      minWidth: '1.25rem',
      paddingTop: '.375rem',
      right: '1.375rem'
    }

  const formik = useFormikContext()
  const conditionalShowAndHideClassName = useHideAndShowField({ formik, fieldName: field.name, ...props })
  const fieldCSSClasses = useFieldCSSClasses(touched, errors, field.name)
  const [counterValue, setCounterValue] = useState(field.value || 0)
  const addItemRef = useRef(null)
  const deleteItemRef = useRef(null)

  useEffect(() => {
    if (isFunction(loadValue)) {
      const val = loadValue(formik.values)
      form.setFieldValue(field.name, val)
      formik.setFieldValue(field.name, val)
    }
    // eslint-disable-next-line
  }, [formik.values])

  useEffect(() => {
    addItemRef.current = debounce(() => {
      let currentVal = !counterValue ? 0 : counterValue

      if (
        controller &&
        siblingsControl &&
        currentVal + getAttr(formik.values, siblingsControl) >= getAttr(formik.values, controller)
      )
        return

      if (maxCounterValue && currentVal >= maxCounterValue) return

      const newValue = Math.round((currentVal + step) * 10) / 10
      setCounterValue(newValue)
      formik.setFieldValue(field.name, newValue)
      form.setFieldValue(field.name, newValue)
    }, 50)

    deleteItemRef.current = debounce(() => {
      let currentVal = !counterValue ? 0 : counterValue

      if (currentVal <= 0) return

      const newValue = Math.round((currentVal - step) * 10) / 10
      setCounterValue(newValue)
      formik.setFieldValue(field.name, newValue)
      form.setFieldValue(field.name, newValue)
    }, 50)

    return () => {
      if (addItemRef.current && addItemRef.current.cancel) {
      addItemRef.current.cancel();
    }
    if (deleteItemRef.current && deleteItemRef.current.cancel) {
      deleteItemRef.current.cancel();
    }
    }
  }, [counterValue, formik.values, step, controller, siblingsControl, maxCounterValue, field.name])

  const handleChange = (e) => {
    if (enableInput) {
      let value = e.target.value
      let parsedValue = parseFloat(value)

      if (!isNaN(parsedValue)) {
        let roundedValue = Math.round(parsedValue * 10) / 10
        setCounterValue(roundedValue)
        formik.setFieldValue(field.name, roundedValue)
        form.setFieldValue(field.name, roundedValue)
        return
      }

      setCounterValue(parsedValue)
      formik.setFieldValue(field.name, value)
      form.setFieldValue(field.name, value)
    }
  }

  return (
    <>
      <div
        style={{ width: '100%' }}
        className={inputGroupClassName + conditionalShowAndHideClassName + ' counter-container'}
      >
        {label && (
          <label className="custom-label-style">
            {label} <span>{required && '*'}</span>
          </label>
        )}
        {unit && <div style={unitStyle}>{unit}</div>}
        <div className="input-group">
          {!enableInput && (
            <div className="input-group-prepend">
              <button
                className="btn btn-danger btn-outline-secondary"
                type="button"
                onClick={() => deleteItemRef.current?.()}
                disabled={disabled || (counterValue || 0) <= 0}
              >
                -
              </button>
            </div>
          )}
          <input
            style={{ backgroundColor: '#FAFAFA', textAlign: 'center', minHeight: '40px' }}
            type={enableInput ? "number" : type}
            step={step}
            disabled={!enableInput}
            className={`${inputClassName} ${fieldCSSClasses}`}
            maxLength={maxLength}
            autoComplete="off"
            placeholder={placeholder}
            {...field}
            onChange={handleChange}
            value={field.value}
          />
          {!enableInput && (
            <div className="input-group-prepend">
              <button
                className="btn btn-danger btn-outline-secondary"
                type="button"
                onClick={() => addItemRef.current?.()}
                disabled={disabled || 
                  (maxCounterValue && (counterValue || 0) >= maxCounterValue) ||
                  (controller && siblingsControl && 
                  (counterValue || 0) + (getAttr(formik.values, siblingsControl) || 0) >= 
                  (getAttr(formik.values, controller) || 0))
                }
              >
                +
              </button>
            </div>
          )}
        </div>
        <FieldError fieldName={field.name} />
      </div>
    </>
  )
}

export default Counter